/**
 * TownData.ts - Town map layouts and configurations
 * Defines all towns in FF1 with tile layouts, building positions, and NPCs
 */

import { Location } from './StoryData';

// ============================================================================
// TOWN TILE TYPES
// ============================================================================

export enum TownTile {
  GRASS = 0,
  STONE_FLOOR = 1,
  WOOD_FLOOR = 2,
  CARPET = 3,
  WATER = 4,
  WALL_STONE = 10,
  WALL_WOOD = 11,
  WALL_BRICK = 12,
  WALL_CASTLE = 13,
  DOOR = 20,
  STAIRS_UP = 21,
  STAIRS_DOWN = 22,
  COUNTER = 30,
  TABLE = 31,
  CHAIR = 32,
  BED = 33,
  CHEST = 34,
  THRONE = 35,
  FOUNTAIN = 36,
  TREE = 40,
  FLOWER = 41,
  SIGN = 42,
}

// ============================================================================
// BUILDING TYPES
// ============================================================================

export enum BuildingType {
  INN = 'INN',
  WEAPON_SHOP = 'WEAPON_SHOP',
  ARMOR_SHOP = 'ARMOR_SHOP',
  ITEM_SHOP = 'ITEM_SHOP',
  MAGIC_SHOP = 'MAGIC_SHOP',
  CLINIC = 'CLINIC',
  HOUSE = 'HOUSE',
}

export interface Building {
  type: BuildingType;
  name: string;
  x: number; // Entrance tile X
  y: number; // Entrance tile Y
  price?: number; // For inn/clinic
  inventory?: number[]; // Item IDs available
}

export interface TownExit {
  x: number;
  y: number;
  direction: 'up' | 'down' | 'left' | 'right';
}

// ============================================================================
// TOWN DEFINITION
// ============================================================================

export interface TownDefinition {
  id: string;
  name: string;
  location: Location;
  width: number;
  height: number;
  tiles: TownTile[][];
  buildings: Building[];
  exits: TownExit[];
  npcPositions: string[]; // NPC IDs from NPCPlacements
  musicTrack?: string;
  theme: 'medieval' | 'coastal' | 'elven' | 'desert' | 'mountain' | 'beach' | 'ancient' | 'castle';
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create an empty town map filled with grass
 */
function createEmptyTown(width: number, height: number): TownTile[][] {
  return Array(height)
    .fill(null)
    .map(() => Array(width).fill(TownTile.GRASS));
}

/**
 * Safely set a tile value
 */
function setTownTile(tiles: TownTile[][], row: number, col: number, tile: TownTile): void {
  const r = tiles[row];
  if (r) {
    r[col] = tile;
  }
}

/**
 * Fill a rectangular area with a tile type
 */
function fillRect(
  tiles: TownTile[][],
  x: number,
  y: number,
  width: number,
  height: number,
  tile: TownTile
): void {
  for (let row = y; row < y + height && row < tiles.length; row++) {
    const firstRow = tiles[0];
    if (!firstRow) continue;
    for (let col = x; col < x + width && col < firstRow.length; col++) {
      if (row >= 0 && col >= 0) {
        setTownTile(tiles, row, col, tile);
      }
    }
  }
}

/**
 * Draw a building outline with walls and door
 */
function drawBuilding(
  tiles: TownTile[][],
  x: number,
  y: number,
  width: number,
  height: number,
  wallType: TownTile = TownTile.WALL_STONE,
  floorType: TownTile = TownTile.STONE_FLOOR
): void {
  // Fill interior with floor
  fillRect(tiles, x + 1, y + 1, width - 2, height - 2, floorType);

  // Draw walls
  for (let row = y; row < y + height; row++) {
    setTownTile(tiles, row, x, wallType); // Left wall
    setTownTile(tiles, row, x + width - 1, wallType); // Right wall
  }
  for (let col = x; col < x + width; col++) {
    setTownTile(tiles, y, col, wallType); // Top wall
    setTownTile(tiles, y + height - 1, col, wallType); // Bottom wall
  }

  // Add door at bottom center
  const doorX = x + Math.floor(width / 2);
  setTownTile(tiles, y + height - 1, doorX, TownTile.DOOR);
}

// ============================================================================
// CORNELIA - Starting Town
// ============================================================================

function createCornelia(): TownDefinition {
  const width = 24;
  const height = 24;
  const tiles = createEmptyTown(width, height);

  // Town center with stone path and fountain
  fillRect(tiles, 10, 10, 4, 4, TownTile.STONE_FLOOR);
  setTownTile(tiles, 12, 12, TownTile.FOUNTAIN);

  // Weapon Shop (northwest) - well spaced from others
  drawBuilding(tiles, 2, 2, 6, 5);
  setTownTile(tiles, 3, 4, TownTile.COUNTER);
  setTownTile(tiles, 3, 5, TownTile.COUNTER);

  // Item Shop (northeast) - more spacing
  drawBuilding(tiles, 16, 2, 6, 5);
  setTownTile(tiles, 3, 18, TownTile.COUNTER);
  setTownTile(tiles, 3, 19, TownTile.COUNTER);

  // Inn (southwest) - more spacing
  drawBuilding(tiles, 2, 17, 6, 5);
  setTownTile(tiles, 18, 3, TownTile.BED);
  setTownTile(tiles, 18, 4, TownTile.BED);
  setTownTile(tiles, 18, 5, TownTile.BED);
  setTownTile(tiles, 18, 6, TownTile.BED);

  // Magic Shop (southeast) - more spacing
  drawBuilding(tiles, 16, 17, 6, 5);
  setTownTile(tiles, 18, 18, TownTile.COUNTER);
  setTownTile(tiles, 18, 19, TownTile.COUNTER);

  // Houses in middle areas with good spacing
  drawBuilding(tiles, 2, 9, 4, 4, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  drawBuilding(tiles, 18, 9, 4, 4, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);

  // Trees for decoration - around edges
  setTownTile(tiles, 1, 1, TownTile.TREE);
  setTownTile(tiles, 1, 22, TownTile.TREE);
  setTownTile(tiles, 22, 1, TownTile.TREE);
  setTownTile(tiles, 22, 22, TownTile.TREE);
  setTownTile(tiles, 1, 11, TownTile.TREE);
  setTownTile(tiles, 22, 11, TownTile.TREE);

  // Sign at town entrance
  setTownTile(tiles, 22, 12, TownTile.SIGN);

  return {
    id: 'CORNELIA',
    name: 'Cornelia',
    location: Location.CORNELIA,
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.WEAPON_SHOP,
        name: 'Weapon Shop',
        x: 4,
        y: 6,
        inventory: [1, 2, 3, 4, 5],
      },
      {
        type: BuildingType.ITEM_SHOP,
        name: 'Item Shop',
        x: 18,
        y: 6,
        inventory: [101, 102, 103],
      },
      {
        type: BuildingType.INN,
        name: 'Cornelia Inn',
        x: 4,
        y: 21,
        price: 50,
      },
      {
        type: BuildingType.MAGIC_SHOP,
        name: 'Magic Shop',
        x: 18,
        y: 21,
        inventory: [201, 202, 203],
      },
    ],
    exits: [
      { x: 12, y: 23, direction: 'down' }, // South exit
      { x: 12, y: 0, direction: 'up' }, // North exit to castle
    ],
    npcPositions: [
      'CORNELIA_OLD_MAN',
      'CORNELIA_WOMAN',
      'CORNELIA_GUARD',
      'CORNELIA_SHOP_KEEPER',
      'CORNELIA_INN_KEEPER',
      'CORNELIA_SCHOLAR',
    ],
    musicTrack: 'town_theme',
    theme: 'medieval',
  };
}

// ============================================================================
// CORNELIA CASTLE
// ============================================================================

function createCorneliaCastle(): TownDefinition {
  const width = 18;
  const height = 16;
  const tiles = createEmptyTown(width, height);

  // Castle floor
  fillRect(tiles, 2, 2, 14, 12, TownTile.CARPET);

  // Throne room walls
  fillRect(tiles, 1, 1, 16, 14, TownTile.WALL_CASTLE);

  // Interior floor
  fillRect(tiles, 2, 2, 14, 12, TownTile.CARPET);

  // Throne at top center
  setTownTile(tiles, 4, 8, TownTile.THRONE);
  setTownTile(tiles, 4, 9, TownTile.THRONE);

  // Guards positions (marked with stone floor)
  setTownTile(tiles, 6, 5, TownTile.STONE_FLOOR);
  setTownTile(tiles, 6, 12, TownTile.STONE_FLOOR);

  // Red carpet path to throne
  for (let y = 8; y < 14; y++) {
    setTownTile(tiles, y, 8, TownTile.CARPET);
    setTownTile(tiles, y, 9, TownTile.CARPET);
  }

  // Entrance door
  setTownTile(tiles, 14, 8, TownTile.DOOR);
  setTownTile(tiles, 14, 9, TownTile.DOOR);

  return {
    id: 'CORNELIA_CASTLE',
    name: 'Cornelia Castle',
    location: Location.CORNELIA_CASTLE,
    width,
    height,
    tiles,
    buildings: [],
    exits: [{ x: 8, y: 15, direction: 'down' }],
    npcPositions: ['KING', 'PRINCESS_SARA', 'CASTLE_MINISTER'],
    musicTrack: 'castle_theme',
    theme: 'castle',
  };
}

// ============================================================================
// PRAVOKA - Coastal Port Town
// ============================================================================

function createPravoka(): TownDefinition {
  const width = 24;
  const height = 24;
  const tiles = createEmptyTown(width, height);

  // Water on the right side (port)
  fillRect(tiles, 19, 0, 5, 24, TownTile.WATER);

  // Stone docks - wider area
  fillRect(tiles, 16, 4, 3, 16, TownTile.STONE_FLOOR);

  // Weapon/Armor Shop (northwest)
  drawBuilding(tiles, 2, 2, 6, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 3, 4, TownTile.COUNTER);
  setTownTile(tiles, 3, 5, TownTile.COUNTER);

  // Inn (west side, mid) - good spacing from other buildings
  drawBuilding(tiles, 2, 9, 7, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 10, 3, TownTile.BED);
  setTownTile(tiles, 10, 4, TownTile.BED);
  setTownTile(tiles, 10, 5, TownTile.BED);
  setTownTile(tiles, 10, 6, TownTile.BED);
  setTownTile(tiles, 10, 7, TownTile.BED);

  // Item Shop (southwest)
  drawBuilding(tiles, 2, 17, 6, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 18, 4, TownTile.COUNTER);
  setTownTile(tiles, 18, 5, TownTile.COUNTER);

  // Tavern (south middle) - separate from item shop
  drawBuilding(tiles, 10, 17, 5, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 18, 11, TownTile.TABLE);
  setTownTile(tiles, 18, 12, TownTile.TABLE);
  setTownTile(tiles, 18, 13, TownTile.TABLE);

  // Lighthouse (stone building, decorative)
  drawBuilding(tiles, 10, 2, 4, 4, TownTile.WALL_STONE);

  return {
    id: 'PRAVOKA',
    name: 'Pravoka',
    location: Location.PRAVOKA,
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.WEAPON_SHOP,
        name: 'Pravoka Weapons',
        x: 4,
        y: 6,
        inventory: [3, 4, 5, 6, 7],
      },
      {
        type: BuildingType.INN,
        name: 'Seaside Inn',
        x: 5,
        y: 13,
        price: 75,
      },
      {
        type: BuildingType.ITEM_SHOP,
        name: 'Port Shop',
        x: 4,
        y: 21,
        inventory: [101, 102, 103, 104],
      },
    ],
    exits: [{ x: 8, y: 23, direction: 'down' }],
    npcPositions: ['PRAVOKA_SAILOR', 'PRAVOKA_WOMAN', 'PRAVOKA_MERCHANT'],
    musicTrack: 'town_theme',
    theme: 'coastal',
  };
}

// ============================================================================
// ELFHEIM - Elven Forest Village
// ============================================================================

function createElfheim(): TownDefinition {
  const width = 24;
  const height = 24;
  const tiles = createEmptyTown(width, height);

  // Forest theme - lots of trees around edges and scattered
  setTownTile(tiles, 1, 1, TownTile.TREE);
  setTownTile(tiles, 1, 6, TownTile.TREE);
  setTownTile(tiles, 1, 17, TownTile.TREE);
  setTownTile(tiles, 1, 22, TownTile.TREE);
  setTownTile(tiles, 6, 1, TownTile.TREE);
  setTownTile(tiles, 6, 22, TownTile.TREE);
  setTownTile(tiles, 17, 1, TownTile.TREE);
  setTownTile(tiles, 17, 22, TownTile.TREE);
  setTownTile(tiles, 22, 6, TownTile.TREE);
  setTownTile(tiles, 22, 12, TownTile.TREE);
  setTownTile(tiles, 22, 17, TownTile.TREE);

  // Central plaza with flowers - larger
  fillRect(tiles, 10, 10, 4, 4, TownTile.GRASS);
  setTownTile(tiles, 11, 11, TownTile.FLOWER);
  setTownTile(tiles, 11, 12, TownTile.FLOWER);
  setTownTile(tiles, 12, 11, TownTile.FLOWER);
  setTownTile(tiles, 12, 12, TownTile.FLOWER);

  // White Magic Shop (northwest) - better spacing
  drawBuilding(tiles, 2, 2, 6, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 3, 4, TownTile.COUNTER);
  setTownTile(tiles, 3, 5, TownTile.COUNTER);

  // Weapon Shop (northeast) - better spacing
  drawBuilding(tiles, 16, 2, 6, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 3, 18, TownTile.COUNTER);
  setTownTile(tiles, 3, 19, TownTile.COUNTER);

  // Inn (southwest) - better spacing
  drawBuilding(tiles, 2, 17, 6, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 18, 3, TownTile.BED);
  setTownTile(tiles, 18, 4, TownTile.BED);
  setTownTile(tiles, 18, 5, TownTile.BED);
  setTownTile(tiles, 18, 6, TownTile.BED);

  // Item Shop (southeast) - better spacing
  drawBuilding(tiles, 16, 17, 6, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 18, 18, TownTile.COUNTER);
  setTownTile(tiles, 18, 19, TownTile.COUNTER);

  // Elven houses (small) - decorative, better placement
  drawBuilding(tiles, 2, 9, 4, 4, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  drawBuilding(tiles, 18, 9, 4, 4, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);

  return {
    id: 'ELFHEIM',
    name: 'Elfheim',
    location: Location.ELFHEIM,
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.MAGIC_SHOP,
        name: 'White Magic Shop',
        x: 4,
        y: 6,
        inventory: [204, 205, 206, 207],
      },
      {
        type: BuildingType.WEAPON_SHOP,
        name: 'Elven Weapons',
        x: 18,
        y: 6,
        inventory: [8, 9, 10, 11],
      },
      {
        type: BuildingType.INN,
        name: 'Elfheim Inn',
        x: 4,
        y: 21,
        price: 100,
      },
      {
        type: BuildingType.ITEM_SHOP,
        name: 'Elven Goods',
        x: 18,
        y: 21,
        inventory: [101, 102, 103, 105],
      },
    ],
    exits: [{ x: 12, y: 23, direction: 'down' }],
    npcPositions: ['ELFHEIM_ELF_1', 'ELFHEIM_ELF_2', 'ELFHEIM_PRINCE'],
    musicTrack: 'town_theme',
    theme: 'elven',
  };
}

// ============================================================================
// MELMOND - Town of Decay
// ============================================================================

function createMelmond(): TownDefinition {
  const width = 20;
  const height = 20;
  const tiles = createEmptyTown(width, height);

  // Darker, more somber town - central stone plaza
  fillRect(tiles, 7, 7, 6, 6, TownTile.STONE_FLOOR);

  // Clinic (northwest) - building from (2,2) to (7,6), door at (4,6)
  // Clinic has beds for patients
  drawBuilding(tiles, 2, 2, 6, 5, TownTile.WALL_BRICK, TownTile.STONE_FLOOR);
  setTownTile(tiles, 3, 3, TownTile.BED); // Patient beds at back
  setTownTile(tiles, 3, 4, TownTile.BED);
  setTownTile(tiles, 3, 5, TownTile.BED);
  setTownTile(tiles, 3, 6, TownTile.BED);

  // Weapon Shop (northeast) - building from (12,2) to (17,6), door at (14,6)
  drawBuilding(tiles, 12, 2, 6, 5, TownTile.WALL_BRICK, TownTile.STONE_FLOOR);
  setTownTile(tiles, 3, 14, TownTile.COUNTER);
  setTownTile(tiles, 3, 15, TownTile.COUNTER);

  // Inn (southwest) - building from (2,13) to (7,17), door at (4,17)
  drawBuilding(tiles, 2, 13, 6, 5, TownTile.WALL_BRICK, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 14, 3, TownTile.BED);
  setTownTile(tiles, 14, 4, TownTile.BED);
  setTownTile(tiles, 14, 5, TownTile.BED);
  setTownTile(tiles, 14, 6, TownTile.BED);

  // Item Shop (southeast) - building from (12,13) to (17,17), door at (14,17)
  drawBuilding(tiles, 12, 13, 6, 5, TownTile.WALL_BRICK, TownTile.STONE_FLOOR);
  setTownTile(tiles, 14, 14, TownTile.COUNTER);
  setTownTile(tiles, 14, 15, TownTile.COUNTER);

  // Graveyard area (tombstones represented as signs)
  setTownTile(tiles, 1, 8, TownTile.SIGN);
  setTownTile(tiles, 1, 9, TownTile.SIGN);
  setTownTile(tiles, 1, 10, TownTile.SIGN);
  setTownTile(tiles, 1, 11, TownTile.SIGN);

  return {
    id: 'MELMOND',
    name: 'Melmond',
    location: Location.MELMOND,
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.CLINIC,
        name: 'Melmond Clinic',
        x: 4,
        y: 6, // Door position
        price: 100,
      },
      {
        type: BuildingType.WEAPON_SHOP,
        name: 'Melmond Weapons',
        x: 14,
        y: 6, // Door position
        inventory: [12, 13, 14, 15],
      },
      {
        type: BuildingType.INN,
        name: 'Melmond Inn',
        x: 4,
        y: 17, // Door position
        price: 120,
      },
      {
        type: BuildingType.ITEM_SHOP,
        name: 'Melmond Shop',
        x: 14,
        y: 17, // Door position
        inventory: [101, 102, 103, 106],
      },
    ],
    exits: [{ x: 10, y: 19, direction: 'down' }],
    npcPositions: ['MELMOND_OLD_MAN', 'MELMOND_SAGE', 'MELMOND_WOMAN'],
    musicTrack: 'town_theme',
    theme: 'medieval',
  };
}

// ============================================================================
// CRESCENT LAKE - Circle of Sages
// ============================================================================

function createCrescentLake(): TownDefinition {
  const width = 22;
  const height = 22;
  const tiles = createEmptyTown(width, height);

  // Crescent-shaped lake in center (smaller so NPCs have room)
  fillRect(tiles, 9, 7, 5, 6, TownTile.WATER);

  // Stone path around the lake for NPCs to walk on
  fillRect(tiles, 7, 5, 9, 1, TownTile.STONE_FLOOR); // North path
  fillRect(tiles, 7, 13, 9, 1, TownTile.STONE_FLOOR); // South path
  fillRect(tiles, 7, 5, 1, 9, TownTile.STONE_FLOOR); // West path
  fillRect(tiles, 15, 5, 1, 9, TownTile.STONE_FLOOR); // East path

  // Four sage houses around the lake
  drawBuilding(tiles, 2, 6, 5, 4, TownTile.WALL_STONE, TownTile.STONE_FLOOR); // West house
  drawBuilding(tiles, 16, 6, 5, 4, TownTile.WALL_STONE, TownTile.STONE_FLOOR); // East house
  drawBuilding(tiles, 8, 1, 6, 4, TownTile.WALL_STONE, TownTile.STONE_FLOOR); // North house
  drawBuilding(tiles, 8, 15, 6, 4, TownTile.WALL_STONE, TownTile.STONE_FLOOR); // South house

  // Inn (southwest) - building from (2,14) to (7,18), door at (4,18)
  drawBuilding(tiles, 2, 14, 6, 5, TownTile.WALL_STONE, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 15, 3, TownTile.BED);
  setTownTile(tiles, 15, 4, TownTile.BED);
  setTownTile(tiles, 15, 5, TownTile.BED);
  setTownTile(tiles, 15, 6, TownTile.BED);

  // Magic Shop (southeast) - building from (14,14) to (19,18), door at (16,18)
  drawBuilding(tiles, 14, 14, 6, 5, TownTile.WALL_STONE, TownTile.STONE_FLOOR);
  setTownTile(tiles, 15, 16, TownTile.COUNTER);
  setTownTile(tiles, 15, 17, TownTile.COUNTER);

  return {
    id: 'CRESCENT_LAKE',
    name: 'Crescent Lake',
    location: Location.CRESCENT_LAKE,
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.INN,
        name: 'Crescent Inn',
        x: 4,
        y: 18, // Door position
        price: 150,
      },
      {
        type: BuildingType.MAGIC_SHOP,
        name: 'Sage Magic',
        x: 16,
        y: 18, // Door position
        inventory: [208, 209, 210, 211], // High-level magic
      },
    ],
    exits: [{ x: 11, y: 21, direction: 'down' }],
    npcPositions: ['ELDER_SAGE', 'SAGE_2', 'SAGE_3', 'SAGE_4'],
    musicTrack: 'town_theme',
    theme: 'desert',
  };
}

// ============================================================================
// ONRAC - Beach Resort
// ============================================================================

function createOnrac(): TownDefinition {
  const width = 20;
  const height = 20;
  const tiles = createEmptyTown(width, height);

  // Beach (right side is ocean)
  fillRect(tiles, 15, 0, 5, 20, TownTile.WATER);

  // Sandy beach walkway (grass) along the shore
  fillRect(tiles, 12, 0, 3, 20, TownTile.GRASS);

  // Inn/Resort (northwest) - building from (2,2) to (8,7), door at (5,7)
  drawBuilding(tiles, 2, 2, 7, 6, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 3, 3, TownTile.BED);
  setTownTile(tiles, 3, 4, TownTile.BED);
  setTownTile(tiles, 3, 5, TownTile.BED);
  setTownTile(tiles, 3, 6, TownTile.BED);
  setTownTile(tiles, 3, 7, TownTile.BED);

  // Item Shop - building from (2,9) to (7,13), door at (4,13)
  drawBuilding(tiles, 2, 9, 6, 5, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 10, 3, TownTile.COUNTER);
  setTownTile(tiles, 10, 4, TownTile.COUNTER);
  setTownTile(tiles, 10, 5, TownTile.COUNTER);
  setTownTile(tiles, 10, 6, TownTile.COUNTER);

  // Magic shop - building from (2,15) to (7,18), door at (4,18)
  drawBuilding(tiles, 2, 15, 6, 4, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 16, 3, TownTile.COUNTER);
  setTownTile(tiles, 16, 4, TownTile.COUNTER);
  setTownTile(tiles, 16, 5, TownTile.COUNTER);
  setTownTile(tiles, 16, 6, TownTile.COUNTER);

  // Small beach houses - decorative
  drawBuilding(tiles, 9, 9, 3, 3, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);
  drawBuilding(tiles, 9, 14, 3, 3, TownTile.WALL_WOOD, TownTile.WOOD_FLOOR);

  return {
    id: 'ONRAC',
    name: 'Onrac',
    location: Location.ONRAC,
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.INN,
        name: 'Onrac Resort',
        x: 5,
        y: 7, // Door position
        price: 175,
      },
      {
        type: BuildingType.ITEM_SHOP,
        name: 'Beach Shop',
        x: 4,
        y: 13, // Door position
        inventory: [101, 102, 107, 108],
      },
      {
        type: BuildingType.MAGIC_SHOP,
        name: 'Onrac Magic',
        x: 4,
        y: 18, // Door position
        inventory: [212, 213, 214],
      },
    ],
    exits: [{ x: 6, y: 19, direction: 'down' }],
    npcPositions: ['ONRAC_WOMAN', 'ONRAC_FISHERMAN'],
    musicTrack: 'town_theme',
    theme: 'beach',
  };
}

// ============================================================================
// GAIA - Northern Mountain Town
// ============================================================================

function createGaia(): TownDefinition {
  const width = 18;
  const height = 18;
  const tiles = createEmptyTown(width, height);

  // Stone plaza in center
  fillRect(tiles, 7, 7, 4, 4, TownTile.STONE_FLOOR);

  // Inn (northwest) - building from (2,2) to (7,6), door at (4,6)
  drawBuilding(tiles, 2, 2, 6, 5, TownTile.WALL_STONE, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 3, 3, TownTile.BED);
  setTownTile(tiles, 3, 4, TownTile.BED);
  setTownTile(tiles, 3, 5, TownTile.BED);
  setTownTile(tiles, 3, 6, TownTile.BED);

  // Armor Shop (northeast) - building from (10,2) to (15,6), door at (12,6)
  drawBuilding(tiles, 10, 2, 6, 5, TownTile.WALL_STONE, TownTile.STONE_FLOOR);
  setTownTile(tiles, 3, 12, TownTile.COUNTER);
  setTownTile(tiles, 3, 13, TownTile.COUNTER);

  // Item Shop (southwest) - building from (2,11) to (7,15), door at (4,15)
  drawBuilding(tiles, 2, 11, 6, 5, TownTile.WALL_STONE, TownTile.STONE_FLOOR);
  setTownTile(tiles, 12, 3, TownTile.COUNTER);
  setTownTile(tiles, 12, 4, TownTile.COUNTER);
  setTownTile(tiles, 12, 5, TownTile.COUNTER);
  setTownTile(tiles, 12, 6, TownTile.COUNTER);

  // House (southeast) - decorative
  drawBuilding(tiles, 10, 11, 6, 5, TownTile.WALL_STONE, TownTile.STONE_FLOOR);

  return {
    id: 'GAIA',
    name: 'Gaia',
    location: Location.GAIA,
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.INN,
        name: 'Mountain Inn',
        x: 4,
        y: 6, // Door position
        price: 150,
      },
      {
        type: BuildingType.ARMOR_SHOP,
        name: 'Gaia Armor',
        x: 12,
        y: 6, // Door position
        inventory: [50, 51, 52, 53],
      },
      {
        type: BuildingType.ITEM_SHOP,
        name: 'Gaia Shop',
        x: 4,
        y: 15, // Door position
        inventory: [101, 102, 103, 109],
      },
    ],
    exits: [{ x: 9, y: 17, direction: 'down' }],
    npcPositions: ['GAIA_MAN', 'GAIA_WOMAN'],
    musicTrack: 'town_theme',
    theme: 'mountain',
  };
}

// ============================================================================
// LUFENIA - Ancient Civilization
// ============================================================================

function createLufenia(): TownDefinition {
  const width = 22;
  const height = 22;
  const tiles = createEmptyTown(width, height);

  // Ancient stone plaza in center
  fillRect(tiles, 9, 9, 4, 4, TownTile.STONE_FLOOR);

  // Large weapon shop (northwest) - building from (2,2) to (8,7), door at (5,7)
  drawBuilding(tiles, 2, 2, 7, 6, TownTile.WALL_BRICK, TownTile.STONE_FLOOR);
  setTownTile(tiles, 3, 4, TownTile.COUNTER);
  setTownTile(tiles, 3, 5, TownTile.COUNTER);
  setTownTile(tiles, 3, 6, TownTile.COUNTER);
  setTownTile(tiles, 3, 7, TownTile.COUNTER);

  // Large armor shop (northeast) - building from (13,2) to (19,7), door at (16,7)
  drawBuilding(tiles, 13, 2, 7, 6, TownTile.WALL_BRICK, TownTile.STONE_FLOOR);
  setTownTile(tiles, 3, 15, TownTile.COUNTER);
  setTownTile(tiles, 3, 16, TownTile.COUNTER);
  setTownTile(tiles, 3, 17, TownTile.COUNTER);
  setTownTile(tiles, 3, 18, TownTile.COUNTER);

  // Magic shop (southwest) - building from (2,14) to (8,19), door at (5,19)
  drawBuilding(tiles, 2, 14, 7, 6, TownTile.WALL_BRICK, TownTile.STONE_FLOOR);
  setTownTile(tiles, 15, 4, TownTile.COUNTER);
  setTownTile(tiles, 15, 5, TownTile.COUNTER);
  setTownTile(tiles, 15, 6, TownTile.COUNTER);
  setTownTile(tiles, 15, 7, TownTile.COUNTER);

  // Inn (southeast) - building from (13,14) to (19,19), door at (16,19)
  drawBuilding(tiles, 13, 14, 7, 6, TownTile.WALL_BRICK, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 15, 14, TownTile.BED);
  setTownTile(tiles, 15, 15, TownTile.BED);
  setTownTile(tiles, 15, 16, TownTile.BED);
  setTownTile(tiles, 15, 17, TownTile.BED);
  setTownTile(tiles, 15, 18, TownTile.BED);

  // Ancient pillars around center plaza
  setTownTile(tiles, 8, 8, TownTile.SIGN);
  setTownTile(tiles, 8, 13, TownTile.SIGN);
  setTownTile(tiles, 13, 8, TownTile.SIGN);
  setTownTile(tiles, 13, 13, TownTile.SIGN);

  return {
    id: 'LUFENIA',
    name: 'Lufenia',
    location: Location.ONRAC, // Using ONRAC location enum for now
    width,
    height,
    tiles,
    buildings: [
      {
        type: BuildingType.WEAPON_SHOP,
        name: 'Ancient Weapons',
        x: 5,
        y: 7, // Door position
        inventory: [20, 21, 22, 23, 24], // Best weapons
      },
      {
        type: BuildingType.ARMOR_SHOP,
        name: 'Ancient Armor',
        x: 16,
        y: 7, // Door position
        inventory: [60, 61, 62, 63, 64], // Best armor
      },
      {
        type: BuildingType.MAGIC_SHOP,
        name: 'Ancient Magic',
        x: 5,
        y: 19, // Door position
        inventory: [215, 216, 217, 218], // Best magic
      },
      {
        type: BuildingType.INN,
        name: 'Ancient Rest',
        x: 16,
        y: 19, // Door position
        price: 200,
      },
    ],
    exits: [{ x: 11, y: 21, direction: 'down' }],
    npcPositions: [],
    musicTrack: 'town_theme',
    theme: 'ancient',
  };
}

// ============================================================================
// ELVEN CASTLE (Northwest Castle - Sleeping Prince)
// ============================================================================

function createElvenCastle(): TownDefinition {
  const width = 20;
  const height = 18;
  const tiles = createEmptyTown(width, height);

  // Castle exterior walls
  fillRect(tiles, 0, 0, width, height, TownTile.GRASS);
  fillRect(tiles, 1, 1, width - 2, height - 2, TownTile.WALL_CASTLE);
  fillRect(tiles, 2, 2, width - 4, height - 4, TownTile.CARPET);

  // Throne room at top
  fillRect(tiles, 2, 8, 3, 4, TownTile.STONE_FLOOR);
  setTownTile(tiles, 3, 9, TownTile.THRONE);
  setTownTile(tiles, 3, 10, TownTile.THRONE);

  // Prince's bed chamber (where he sleeps)
  fillRect(tiles, 2, 13, 4, 5, TownTile.WOOD_FLOOR);
  setTownTile(tiles, 4, 14, TownTile.BED); // Sleeping prince here
  setTownTile(tiles, 4, 16, TownTile.CHEST); // Mystic Key chest

  // Guard posts
  setTownTile(tiles, 6, 6, TownTile.STONE_FLOOR);
  setTownTile(tiles, 6, 13, TownTile.STONE_FLOOR);

  // Main hall with carpet
  for (let y = 8; y < 16; y++) {
    setTownTile(tiles, y, 9, TownTile.CARPET);
    setTownTile(tiles, y, 10, TownTile.CARPET);
  }

  // Side rooms (locked with MYSTIC_KEY)
  // Left treasure room
  fillRect(tiles, 10, 2, 4, 4, TownTile.STONE_FLOOR);
  setTownTile(tiles, 12, 3, TownTile.CHEST);
  setTownTile(tiles, 12, 4, TownTile.CHEST);

  // Right treasure room
  fillRect(tiles, 10, 14, 4, 4, TownTile.STONE_FLOOR);
  setTownTile(tiles, 12, 15, TownTile.CHEST);
  setTownTile(tiles, 12, 16, TownTile.CHEST);

  // Entrance
  setTownTile(tiles, 16, 9, TownTile.DOOR);
  setTownTile(tiles, 16, 10, TownTile.DOOR);

  // Decorative pillars
  setTownTile(tiles, 8, 5, TownTile.FOUNTAIN);
  setTownTile(tiles, 8, 14, TownTile.FOUNTAIN);

  return {
    id: 'ELVEN_CASTLE',
    name: 'Elven Castle',
    location: Location.NORTHWEST_CASTLE,
    width,
    height,
    tiles,
    buildings: [],
    exits: [{ x: 9, y: 17, direction: 'down' }],
    npcPositions: ['ELF_KING', 'ELF_PRINCE', 'ELF_CHANCELLOR', 'ELF_GUARD_1', 'ELF_GUARD_2'],
    musicTrack: 'castle_theme',
    theme: 'elven',
  };
}

// ============================================================================
// WESTERN KEEP (Astos' Castle - Fake King)
// ============================================================================

function createWesternKeep(): TownDefinition {
  const width = 16;
  const height = 14;
  const tiles = createEmptyTown(width, height);

  // Ruined exterior - mix of grass and stone
  fillRect(tiles, 0, 0, width, height, TownTile.GRASS);

  // Crumbling walls
  fillRect(tiles, 1, 1, width - 2, height - 2, TownTile.WALL_STONE);
  fillRect(tiles, 2, 2, width - 4, height - 4, TownTile.STONE_FLOOR);

  // Throne room (where Astos sits disguised as king)
  fillRect(tiles, 2, 6, 3, 4, TownTile.CARPET);
  setTownTile(tiles, 3, 7, TownTile.THRONE);

  // Red carpet to throne
  for (let y = 6; y < 12; y++) {
    setTownTile(tiles, y, 7, TownTile.CARPET);
    setTownTile(tiles, y, 8, TownTile.CARPET);
  }

  // Dusty corners (abandoned feel)
  setTownTile(tiles, 3, 3, TownTile.CHEST); // Empty chest
  setTownTile(tiles, 3, 12, TownTile.CHEST); // Empty chest
  setTownTile(tiles, 10, 3, TownTile.TABLE);
  setTownTile(tiles, 10, 12, TownTile.TABLE);

  // Entrance
  setTownTile(tiles, 12, 7, TownTile.DOOR);
  setTownTile(tiles, 12, 8, TownTile.DOOR);

  return {
    id: 'WESTERN_KEEP',
    name: 'Western Keep',
    location: Location.WESTERN_KEEP,
    width,
    height,
    tiles,
    buildings: [],
    exits: [{ x: 7, y: 13, direction: 'down' }],
    npcPositions: ['ASTOS_DISGUISED'],
    musicTrack: 'castle_theme',
    theme: 'castle',
  };
}

// ============================================================================
// TOWN REGISTRY
// ============================================================================

export const TOWN_DEFINITIONS: Record<string, TownDefinition> = {
  CORNELIA: createCornelia(),
  CORNELIA_CASTLE: createCorneliaCastle(),
  PRAVOKA: createPravoka(),
  ELFHEIM: createElfheim(),
  ELVEN_CASTLE: createElvenCastle(),
  WESTERN_KEEP: createWesternKeep(),
  MELMOND: createMelmond(),
  CRESCENT_LAKE: createCrescentLake(),
  ONRAC: createOnrac(),
  GAIA: createGaia(),
  LUFENIA: createLufenia(),
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get town definition by ID
 */
export function getTownById(townId: string): TownDefinition | null {
  return TOWN_DEFINITIONS[townId] || null;
}

/**
 * Get town definition by location
 */
export function getTownByLocation(location: Location): TownDefinition | null {
  return Object.values(TOWN_DEFINITIONS).find((town) => town.location === location) || null;
}

/**
 * Check if tile is walkable in town
 */
export function isTownTileWalkable(tile: TownTile): boolean {
  switch (tile) {
    case TownTile.GRASS:
    case TownTile.STONE_FLOOR:
    case TownTile.WOOD_FLOOR:
    case TownTile.CARPET:
    case TownTile.DOOR:
      return true;
    default:
      return false;
  }
}

/**
 * Check if tile is a building entrance
 */
export function isBuildingEntrance(tile: TownTile): boolean {
  return tile === TownTile.DOOR;
}

/**
 * Get building at position
 */
export function getBuildingAt(town: TownDefinition, x: number, y: number): Building | null {
  return (
    town.buildings.find((building) => {
      // Check if position is at building entrance (door)
      return building.x === x && building.y === y;
    }) || null
  );
}

/**
 * Check if position is an exit
 */
export function isExit(town: TownDefinition, x: number, y: number): boolean {
  return town.exits.some((exit) => exit.x === x && exit.y === y);
}

/**
 * Get exit at position
 */
export function getExitAt(town: TownDefinition, x: number, y: number): TownExit | null {
  return town.exits.find((exit) => exit.x === x && exit.y === y) || null;
}
